import { useCallback, useEffect, useState } from "react";
import { BadgeCheck, Pencil, Plus, ShieldCheck, UserRound } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { PageHeader, Loading } from "../../components/shared";
import { RBAC_ACTIONS, RBAC_MODULES, RBAC_SCOPES } from "../../lib/rbac";
import { toast } from "sonner";

const emptyStaff = () => ({
  name: "",
  email: "",
  password: "",
  employee_id: "",
  department_id: "",
  designation: "",
  reporting_manager_id: "",
  joining_date: "",
  staff_status: "Active",
  phone: "",
  notes: "",
  rbac_role_id: "",
  permission_overrides: {},
  scope_override: "",
  disabled: false,
});

export default function AdminStaff() {
  const [staff, setStaff] = useState(null);
  const [options, setOptions] = useState({ departments: [], roles: [], managers: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStaff());
  const [tab, setTab] = useState("profile");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [staffResponse, optionResponse] = await Promise.all([
        api.get("/admin/staff"),
        api.get("/admin/staff/options"),
      ]);
      setStaff(staffResponse.data);
      setOptions(optionResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load staff");
      setStaff([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setValue = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const openCreate = () => {
    setEditing(null);
    setForm(emptyStaff());
    setTab("profile");
    setDialogOpen(true);
  };
  const openEdit = (person) => {
    setEditing(person);
    setForm({ ...emptyStaff(), ...person, password: "" });
    setTab("profile");
    setDialogOpen(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        const { email, password, ...payload } = form;
        await api.patch(`/admin/staff/${editing.user_id}`, payload);
        toast.success("Staff profile updated");
      } else {
        await api.post("/admin/staff", form);
        toast.success("Staff login created");
      }
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save staff profile");
    } finally {
      setBusy(false);
    }
  };
  const toggleLogin = async (person) => {
    try {
      await api.patch(`/admin/staff/${person.user_id}`, { disabled: !person.disabled });
      toast.success(person.disabled ? "Staff login enabled" : "Staff login disabled");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update staff login");
    }
  };
  const setOverride = (module, action, value) => {
    setForm((current) => {
      const overrides = structuredClone(current.permission_overrides || {});
      if (!overrides[module]) overrides[module] = {};
      if (value === "") delete overrides[module][action];
      else overrides[module][action] = value === "allow";
      if (Object.keys(overrides[module]).length === 0) delete overrides[module];
      return { ...current, permission_overrides: overrides };
    });
  };

  if (!staff) return <Loading />;

  return (
    <div data-testid="admin-staff-page">
      <PageHeader
        title="Internal Staff"
        subtitle="Create platform staff accounts, define reporting relationships, and adjust access when needed."
      >
        <Button className="rounded-full gap-2" data-testid="staff-create-button" onClick={openCreate}>
          <Plus className="w-4 h-4" />
          Add Staff
        </Button>
      </PageHeader>

      <div className="bg-card border rounded-xl overflow-x-auto shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid="staff-table">
        <table className="w-full min-w-[960px] text-sm">
          <thead className="bg-muted/60 text-left">
            <tr>
              <th className="p-3 font-semibold">Staff member</th>
              <th className="p-3 font-semibold">Employee ID</th>
              <th className="p-3 font-semibold">Department</th>
              <th className="p-3 font-semibold">Role</th>
              <th className="p-3 font-semibold">Manager</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((person) => (
              <tr className="border-t" data-testid={`staff-row-${person.user_id}`} key={person.user_id}>
                <td className="p-3">
                  <div className="font-medium" data-testid={`staff-name-${person.user_id}`}>{person.name}</div>
                  <div className="text-xs text-muted-foreground">{person.email}</div>
                </td>
                <td className="p-3 font-mono text-xs">{person.employee_id || "—"}</td>
                <td className="p-3">{person.department_name || "Unassigned"}</td>
                <td className="p-3">{person.rbac_role_name || "No access role"}</td>
                <td className="p-3">{person.reporting_manager_name || "—"}</td>
                <td className="p-3">
                  <span
                    className={person.disabled ? "text-xs text-red-700" : "text-xs text-emerald-700"}
                    data-testid={`staff-status-${person.user_id}`}
                  >
                    {person.disabled ? "Login disabled" : person.staff_status || "Active"}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-2">
                    <Button
                      aria-label={`Edit ${person.name}`}
                      data-testid={`staff-edit-${person.user_id}`}
                      onClick={() => openEdit(person)}
                      size="sm"
                      variant="outline"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      data-testid={`staff-login-toggle-${person.user_id}`}
                      onClick={() => toggleLogin(person)}
                      size="sm"
                      variant="ghost"
                    >
                      {person.disabled ? "Enable" : "Disable"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {staff.length === 0 && (
        <div className="border border-dashed rounded-xl p-12 text-center text-muted-foreground" data-testid="staff-empty-state">
          Add an internal staff member after creating a custom role.
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="staff-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "The assigned role defines baseline access; overrides can grant or deny individual actions."
                : "A new internal staff login uses the existing platform sign-in flow."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 border-b pb-3">
            <Button
              data-testid="staff-profile-tab"
              onClick={() => setTab("profile")}
              size="sm"
              variant={tab === "profile" ? "default" : "ghost"}
            >
              <UserRound className="w-3.5 h-3.5" />
              Profile
            </Button>
            <Button
              data-testid="staff-overrides-tab"
              onClick={() => setTab("overrides")}
              size="sm"
              variant={tab === "overrides" ? "default" : "ghost"}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Access Overrides
            </Button>
          </div>
          {tab === "profile" ? (
            <StaffProfile form={form} setValue={setValue} options={options} editing={Boolean(editing)} />
          ) : (
            <OverrideMatrix form={form} setOverride={setOverride} setValue={setValue} />
          )}
          <DialogFooter>
            <Button data-testid="staff-save-button" disabled={busy} onClick={save}>
              {busy ? "Saving…" : editing ? "Save Changes" : "Create Staff Login"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StaffProfile({ form, setValue, options, editing }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Field id="staff-name" label="Full name">
        <Input data-testid="staff-name-input" id="staff-name" onChange={(e) => setValue("name", e.target.value)} value={form.name} />
      </Field>
      <Field id="staff-employee-id" label="Employee ID">
        <Input data-testid="staff-employee-id-input" id="staff-employee-id" onChange={(e) => setValue("employee_id", e.target.value)} value={form.employee_id} />
      </Field>
      <Field id="staff-email" label="Email">
        <Input data-testid="staff-email-input" disabled={editing} id="staff-email" onChange={(e) => setValue("email", e.target.value)} type="email" value={form.email} />
      </Field>
      {!editing && (
        <Field id="staff-password" label="Temporary password">
          <Input data-testid="staff-password-input" id="staff-password" onChange={(e) => setValue("password", e.target.value)} type="text" value={form.password} />
        </Field>
      )}
      <Field id="staff-department" label="Department">
        <select className="form-select" data-testid="staff-department-select" id="staff-department" onChange={(e) => setValue("department_id", e.target.value)} value={form.department_id || ""}>
          <option value="">Unassigned</option>
          {options.departments.map((department) => <option key={department.department_id} value={department.department_id}>{department.name}</option>)}
        </select>
      </Field>
      <Field id="staff-role" label="Custom role">
        <select className="form-select" data-testid="staff-role-select" id="staff-role" onChange={(e) => setValue("rbac_role_id", e.target.value)} value={form.rbac_role_id || ""}>
          <option value="">No access role</option>
          {options.roles.map((role) => <option key={role.rbac_role_id} value={role.rbac_role_id}>{role.name}</option>)}
        </select>
      </Field>
      <Field id="staff-designation" label="Designation">
        <Input data-testid="staff-designation-input" id="staff-designation" onChange={(e) => setValue("designation", e.target.value)} value={form.designation || ""} />
      </Field>
      <Field id="staff-manager" label="Reporting manager">
        <select className="form-select" data-testid="staff-manager-select" id="staff-manager" onChange={(e) => setValue("reporting_manager_id", e.target.value)} value={form.reporting_manager_id || ""}>
          <option value="">Not assigned</option>
          {options.managers.map((manager) => <option key={manager.user_id} value={manager.user_id}>{manager.name}</option>)}
        </select>
      </Field>
      <Field id="staff-joining-date" label="Joining date">
        <Input data-testid="staff-joining-date-input" id="staff-joining-date" onChange={(e) => setValue("joining_date", e.target.value)} type="date" value={form.joining_date || ""} />
      </Field>
      <Field id="staff-status" label="Employment status">
        <select className="form-select" data-testid="staff-status-select" id="staff-status" onChange={(e) => setValue("staff_status", e.target.value)} value={form.staff_status || "Active"}>
          <option value="Active">Active</option>
          <option value="On Leave">On Leave</option>
          <option value="Inactive">Inactive</option>
        </select>
      </Field>
      <Field id="staff-phone" label="Phone">
        <Input data-testid="staff-phone-input" id="staff-phone" onChange={(e) => setValue("phone", e.target.value)} value={form.phone || ""} />
      </Field>
      <Field id="staff-notes" label="Internal notes">
        <Input data-testid="staff-notes-input" id="staff-notes" onChange={(e) => setValue("notes", e.target.value)} value={form.notes || ""} />
      </Field>
    </div>
  );
}

function OverrideMatrix({ form, setOverride, setValue }) {
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-[1fr_250px] gap-4">
        <div className="text-sm text-muted-foreground leading-relaxed">
          Keep an action on inherit to follow the assigned role. Allow and deny take priority for this person only.
        </div>
        <Field id="staff-scope-override" label="Data scope override">
          <select className="form-select" data-testid="staff-scope-override-select" id="staff-scope-override" onChange={(e) => setValue("scope_override", e.target.value || null)} value={form.scope_override || ""}>
            <option value="">Inherit assigned role</option>
            {RBAC_SCOPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
      </div>
      <div className="border rounded-xl overflow-x-auto" data-testid="staff-override-matrix">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-muted/60 text-left"><tr>
            <th className="p-3 font-semibold">Platform module</th>
            {RBAC_ACTIONS.map((action) => <th className="p-3 font-semibold capitalize" key={action}>{action}</th>)}
          </tr></thead>
          <tbody>
            {RBAC_MODULES.map(([module, label]) => (
              <tr className="border-t" key={module}>
                <td className="p-3 font-medium">{label}</td>
                {RBAC_ACTIONS.map((action) => {
                  const value = form.permission_overrides?.[module]?.[action];
                  const selected = value === true ? "allow" : value === false ? "deny" : "";
                  return (
                    <td className="p-2" key={action}>
                      <select className="form-select h-8 text-xs" data-testid={`staff-override-${module}-${action}`} onChange={(e) => setOverride(module, action, e.target.value)} value={selected}>
                        <option value="">Inherit</option>
                        <option value="allow">Allow</option>
                        <option value="deny">Deny</option>
                      </select>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ id, label, children }) {
  return <div><Label htmlFor={id}>{label}</Label>{children}</div>;
}